import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  HiCheck,
  HiChevronRight,
  HiSearch,
  HiX,
  HiSelector,
} from "react-icons/hi";

const CustomDropdownMenu = ({
  title = "Select Options",
  categories = {},
  selectedItems = [],
  onSelectionChange = () => {},
  placeholder = "Search...",
  maxHeight = "500px",
  maxWidth = "600px",
  disabled = false,
  allowMultiple = true,
  showClearButton = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(
    Object.keys(categories)[0] || "",
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    openUpward: false,
  });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (Object.keys(categories).length > 0 && !selectedCategory) {
      setSelectedCategory(Object.keys(categories)[0]);
    }
  }, [categories, selectedCategory]);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft =
        window.pageXOffset || document.documentElement.scrollLeft;
      const viewportHeight = window.innerHeight;

      // Parse maxHeight to get numeric value
      const dropdownHeight = parseInt(maxHeight);
      const dropdownWidth = parseInt(maxWidth);

      // Calculate space above and below the button
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Determine if we should open upward or downward
      const shouldOpenUpward =
        spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

      // Calculate horizontal position, ensuring it doesn't go off-screen
      const idealLeft = rect.left + scrollLeft;
      const maxLeft = window.innerWidth - dropdownWidth;
      const finalLeft = Math.min(idealLeft, maxLeft);

      let finalTop;
      if (shouldOpenUpward) {
        // Position above the button
        finalTop = rect.top + scrollTop - dropdownHeight - 8;
      } else {
        // Position below the button
        finalTop = rect.bottom + scrollTop + 8;
      }

      setDropdownPosition({
        top: Math.max(8, finalTop), // Ensure it's never too close to the top edge
        left: Math.max(8, finalLeft), // Ensure it's never too close to the left edge
        openUpward: shouldOpenUpward,
      });
    }
  }, [isOpen, maxHeight, maxWidth]);

  const toggleItem = (item) => {
    if (allowMultiple) {
      const newSelection = selectedItems.includes(item)
        ? selectedItems.filter((i) => i !== item)
        : [...selectedItems, item];
      onSelectionChange(newSelection);
    } else {
      onSelectionChange(selectedItems.includes(item) ? [] : [item]);
      setIsOpen(false); // Close dropdown after single selection
    }
  };

  const clearSelection = (e) => {
    e.stopPropagation(); // Prevent opening the dropdown when clicking clear
    onSelectionChange([]);
  };

  const filteredCategories = searchTerm
    ? Object.keys(categories).filter(
        (cat) =>
          cat.toLowerCase().includes(searchTerm.toLowerCase()) ||
          categories[cat].some((item) =>
            (typeof item === "string" ? item : item.label || item.name || "")
              .toLowerCase()
              .includes(searchTerm.toLowerCase()),
          ),
      )
    : Object.keys(categories);

  const getItemLabel = (item) => {
    if (typeof item === "string") return item;
    return item.label || item.name || item.toString();
  };

  const getItemValue = (item) => {
    if (typeof item === "string") return item;
    return item.value || item.id || item.name || item;
  };

  // When searching, if the selected category has no matching items, show the
  // first category that does — prevents the right panel staying empty while
  // results exist in other categories.
  const activeCategory = searchTerm
    ? filteredCategories.includes(selectedCategory)
      ? selectedCategory
      : filteredCategories[0] || selectedCategory
    : selectedCategory;

  const filteredItems =
    searchTerm && activeCategory
      ? categories[activeCategory]?.filter((item) =>
          getItemLabel(item).toLowerCase().includes(searchTerm.toLowerCase()),
        ) || []
      : categories[activeCategory] || [];

  const dropdownContent = isOpen ? (
    <>
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 z-40 bg-transparent"
        onClick={() => setIsOpen(false)}
      />

      {/* Dropdown content */}
      <div
        ref={dropdownRef}
        className={`fixed z-50 flex overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl ${
          dropdownPosition.openUpward
            ? "animate-slide-up"
            : "animate-slide-down"
        }`}
        style={{
          width: maxWidth,
          height: maxHeight,
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          maxHeight: `calc(100vh - ${dropdownPosition.top + 16}px)`, // Ensure it never exceeds viewport
        }}
      >
        {/* Left Column - Categories */}
        <div className="flex w-1/2 flex-col border-r border-gray-200">
          {/* Search Input */}
          <div className="flex-shrink-0 border-b border-gray-100 p-3">
            <div className="relative">
              <HiSearch
                className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
                size={16}
              />
              <input
                type="text"
                placeholder={placeholder}
                className="w-full rounded-md border border-gray-200 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>
          </div>

          {/* Category List */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="sticky top-0 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
              Categories
            </div>
            {filteredCategories.map((category) => {
              const hasSelectedItems = categories[category]?.some((item) =>
                selectedItems.includes(getItemValue(item)),
              );
              return (
                <div
                  key={category}
                  className={`flex cursor-pointer items-center justify-between px-3 py-2 text-sm transition-colors ${
                    activeCategory === category
                      ? "border-r-2 border-blue-500 bg-blue-50 text-blue-700"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                  onClick={() => setSelectedCategory(category)}
                >
                  <span className="flex items-center gap-2">
                    {hasSelectedItems && (
                      <HiCheck size={14} className="text-blue-600" />
                    )}
                    <span className={!hasSelectedItems ? "ml-5" : ""}>
                      {category}
                    </span>
                  </span>
                  <HiChevronRight size={16} className="text-gray-400" />
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Column - Items */}
        <div className="flex w-1/2 flex-col">
          {/* Header */}
          <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-100 bg-gray-50 p-3">
            <h3 className="text-sm font-semibold text-gray-700">
              {activeCategory}
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 transition-colors hover:text-gray-600"
            >
              <HiX size={16} />
            </button>
          </div>

          {/* Items List */}
          <div className="min-h-0 flex-1 overflow-y-auto p-1">
            {filteredItems.map((item) => {
              const itemValue = getItemValue(item);
              const itemLabel = getItemLabel(item);
              const isSelected = selectedItems.includes(itemValue);

              return (
                <div
                  key={itemValue}
                  className="group flex cursor-pointer items-start rounded px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  onClick={() => toggleItem(itemValue)}
                >
                  {/* Checkbox - Fixed width and positioned at the start */}
                  <div className="mr-3 mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border border-gray-300">
                    {isSelected && (
                      <HiCheck size={12} className="text-blue-600" />
                    )}
                  </div>

                  {/* Text content - Takes remaining space */}
                  <span className="flex-1 break-words leading-relaxed">
                    {itemLabel}
                  </span>
                </div>
              );
            })}
            {filteredItems.length === 0 && (
              <div className="px-3 py-8 text-center text-sm text-gray-500">
                No items available
              </div>
            )}
          </div>

          {/* Footer with selected count */}
          <div className="flex-shrink-0 border-t border-gray-100 bg-gray-50 p-3 text-xs text-gray-500">
            {selectedItems.length} item{selectedItems.length !== 1 ? "s" : ""}{" "}
            selected
          </div>
        </div>
      </div>
    </>
  ) : null;

  return (
    <div className="flex w-full">
      <button
        ref={buttonRef}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`flex flex-1 items-center gap-2 px-4 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
          disabled
            ? "cursor-not-allowed border border-gray-300 bg-gray-300 text-gray-500"
            : "border border-gray-300 bg-white text-black hover:bg-gray-200"
        } ${
          showClearButton && selectedItems.length > 0
            ? "rounded-l-lg"
            : "rounded-lg"
        }`}
      >
        <HiSelector size={16} />
        {title}
        {allowMultiple && selectedItems.length > 0 && (
          <span className="h-4 w-4 rounded-full bg-teal-500 text-xs text-white">
            {selectedItems.length}
          </span>
        )}
      </button>

      {/* Clear button - seamlessly attached */}
      {showClearButton && selectedItems.length > 0 && !disabled && (
        <button
          onClick={clearSelection}
          className="rounded-r-lg border-l bg-teal-500 px-1.5 py-2 text-white transition-colors hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          title="Clear selection"
        >
          <HiX size={14} />
        </button>
      )}
      {typeof document !== "undefined" &&
        createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default CustomDropdownMenu;
